import { useEffect, useState } from 'react'

/**
 * Thème de l'interface : clair ou sombre, choisi par l'utilisateur.
 *
 * Le thème est posé sur <html data-theme="…"> : toutes les couleurs passent
 * par les variables CSS, redéfinies pour le sombre dans theme-dark.css.
 */
export type ThemePreference = 'light' | 'dark'
export type EffectiveTheme = ThemePreference

const STORAGE_KEY = 'icc_theme'
const EVENT = 'icc-theme-change'

/** Clair par défaut, y compris pour une ancienne valeur « système » mémorisée. */
export function readPreference(): ThemePreference {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

/** À appeler avant le premier rendu, pour éviter un flash de thème clair. */
export function applyTheme(theme: ThemePreference = readPreference()) {
  document.documentElement.dataset.theme = theme
  document.documentElement.style.colorScheme = theme
}

export function setPreference(theme: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* stockage indisponible : le choix vaut pour la session */
  }
  applyTheme(theme)
  window.dispatchEvent(new CustomEvent(EVENT))
}

/** Thème courant, mis à jour quand l'utilisateur en change. */
export function useTheme(): { preference: ThemePreference; theme: EffectiveTheme; setPreference: (p: ThemePreference) => void } {
  const [theme, setTheme] = useState<ThemePreference>(readPreference)

  useEffect(() => {
    const sync = () => setTheme(readPreference())
    window.addEventListener(EVENT, sync)
    return () => window.removeEventListener(EVENT, sync)
  }, [])

  return { preference: theme, theme, setPreference }
}
